import {
  Input,
  Checkbox,
  Button,
  Select,
  Option,
} from "@material-tailwind/react";
import Datepicker from "react-tailwindcss-datepicker";
import { useForm, Controller } from "react-hook-form";

import TicketTable from "../../Component/TicketTable";
import useDatePicker from "../../hook/useDatePicker";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import service from "../../service";
import { memo, NamedExoticComponent, useState } from "react";
import Loading from "../../Component/Loading";
import Pagination from "../../Component/Pagination";
import { sportTypes, SportType } from "../../constant";

export type FooterProps = {
  betAmount: number;
  realbetAmount: number;
  tickets: number;
  winLose: number;
};

const Footer: NamedExoticComponent<FooterProps> = memo(
  ({ betAmount, realbetAmount, tickets, winLose }) => (
    <tr className="font-bold">
      <td colSpan={2}>Total</td>
      <td></td>
      <td>{realbetAmount && Number(realbetAmount.toFixed(2)).toLocaleString("en-US")}</td>
      <td>{betAmount && Number(betAmount.toFixed(2)).toLocaleString("en-US")}</td>
      <td></td>
      <td>{winLose && Number(winLose.toFixed(2)).toLocaleString("en-US")}</td>
      <td colSpan={4}></td>
    </tr>
  )
);

const Ticket = () => {
  const [searchParams] = useSearchParams();
  const statusOptions = ["waiting", "rejected", "running", "done", "cancelled"];
  const { register, handleSubmit, watch, formState, control } = useForm({
    defaultValues: {
      betId: searchParams.get("betId") || "",
      username: searchParams.get("username") || "",
      status: [false, false, false, true, false],
      dateField: searchParams.get("dateField") || "createDate",
      isAbnormal: false,
      sportType: searchParams.get("sportType") || "",
    },
  });
  const watchStatus = watch("status");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const { value, handleChange } = useDatePicker(
    searchParams.get("startDate")
      ? {
          startDate: dayjs(searchParams.get("startDate"), "DD-MM-YYYY")
            .startOf("day")
            .toDate(),
          endDate: dayjs(searchParams.get("endDate"), "DD-MM-YYYY")
            .startOf("day")
            .toDate(),
        }
      : undefined
  );

  const [queryParam, setQueryParam] = useState({
    username: searchParams.get("username") || "",
    betId: searchParams.get("betId") || "",
    startDate: value.startDate.toISOString() || "",
    endDate: value.endDate.toISOString() || "",
    status: ["done"],
    dateField: searchParams.get("dateField") || "createDate",
    isAbnormal: false,
    sportType: searchParams.get("sportType") || ""
  });

  const ticket = useQuery({
    queryFn: () =>
      service.getTicket({
        ...queryParam,
        page,
        limit,
      }),
    queryKey: [
      "ticket",
      queryParam.startDate,
      queryParam.endDate,
      queryParam.username,
      queryParam.betId,
      queryParam.status,
      page,
      limit,
      queryParam.dateField,
      queryParam.isAbnormal,
      queryParam.sportType,
    ],
    enabled: formState.isSubmitted,
  });

  return (
    <div className="min-w-0 space-y-5">
      <form
        className="grid grid-cols-1 gap-4 rounded-2xl border border-blue-gray-100 bg-white p-4 shadow-sm sm:grid-cols-2 sm:p-5"
        onSubmit={handleSubmit((data) => {
          const selectedStatuses = (data.status as boolean[])
            .map((checked, idx) => (checked ? statusOptions[idx] : null))
            .filter((s): s is string => Boolean(s));
          setPage(1);
          setQueryParam({
            username: data.username,
            betId: data.betId,
            startDate: value.startDate.toISOString(),
            endDate: value.endDate.toISOString(),
            status: selectedStatuses,
            dateField: data.dateField,
            isAbnormal: data.isAbnormal,
            sportType: data.sportType,
          });
        })}
      >
        <Input
          label="Bet ID"
          className="bg-white"
          crossOrigin={undefined}
          {...register("betId")}
        />
        <Input
          label="Username"
          className="bg-white"
          crossOrigin={undefined}
          {...register("username")}
        />

          <Controller
            control={control}
            name="dateField"
            render={({ field }) => (
              <Select {...field} label="Date field" className="bg-white">
                <Option value="createDate">Create Date</Option>
                <Option value="settleDate">Settle Date</Option>
              </Select>
            )}
          />
          <div className="min-w-0 w-full">
            <Datepicker
              primaryColor="green"
              value={value}
              onChange={handleChange}
              showShortcuts
              separator="to"
              displayFormat="DD-MM-YYYY"
              maxDate={dayjs().endOf("month").toDate()}
            />
          </div>

        <Controller
            control={control}
            name="sportType"
            render={({ field }) => (
              <Select {...field} label="Sport Type" className="bg-white">
             {sportTypes.map((s: SportType) => (
               <Option key={s.value} value={s.value}>
                 {s.label}
               </Option>
             ))}
              </Select>
            )}
          />

        <div className="col-span-1 flex flex-wrap items-center gap-x-1 gap-y-0 sm:col-span-2">
          {statusOptions.map((statusVal, idx) => (
            <Controller
              key={statusVal}
              control={control}
              name={`status.${idx}`}
              render={({ field: { onChange, ref, value } }) => (
                <Checkbox
                  crossOrigin={undefined}
                  color="blue"
                  checked={watchStatus[idx]}
                  label={statusVal}
                  ref={ref}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          ))}
          <Controller
            control={control}
            name="isAbnormal"
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                crossOrigin={undefined}
                color="blue"
                checked={Boolean(value)}
                label="Abnormal"
                ref={ref}
                onChange={(e) => onChange(e.target.checked)}
              />
            )}
          />
        </div>

        <div className="col-span-1 sm:col-span-2">
          <Button type="submit" className="w-full sm:w-auto">Submit</Button>
        </div>
      </form>

      {formState.isSubmitted &&
        (ticket.isLoading ? (
          <Loading className="m-auto" />
        ) : (
          <>
            <Pagination
              page={page}
              limit={limit}
              total={ticket.data?.total?.count || 0}
              onClick={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />

            <div className="min-w-0 w-full">
              <TicketTable
                Footer={<Footer
                  betAmount={ticket.data?.total?.betAmt || 0}
                  realbetAmount={ticket.data?.total?.realBetAmt || 0}
                  tickets={ticket.data?.total?.count || 0}
                  winLose={ticket.data?.total?.winLose || 0}
                />}
                data={ticket.data?.data}
                context={{ page, limit }}
              />
            </div>

            <Pagination
              page={page}
              limit={limit}
              total={ticket.data?.total?.count || 0}
              onClick={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          </>
        ))}
    </div>
  );
};

export default Ticket;
